import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import Header from '../../components/Header';

const inputSx = (colors) => ({
  backgroundColor: colors.primary[400],
  color: colors.grey[100],
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[500] },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[500] },
  '& .MuiSvgIcon-root': { color: colors.grey[200] },
});

const labelSx = (colors) => ({
  color: colors.grey[200],
  '&.Mui-focused': { color: colors.greenAccent[400] },
});

const StatCard = ({ icon: Icon, label, value, colors }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
      border: `1px solid ${colors.grey[700]}`,
      borderLeft: `4px solid ${colors.greenAccent[500]}`,
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={1.5}>
      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${colors.blueAccent[700]}88`, color: colors.greenAccent[400], display: 'flex' }}>
        <Icon fontSize="small" />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: colors.grey[300], textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100], mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const gridShellSx = (colors) => ({
  '& .MuiDataGrid-root': { border: 'none' },
  '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}` },
  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none', color: colors.grey[100] },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
  '& .MuiDataGrid-row': { color: colors.grey[100], '&:hover': { backgroundColor: `${colors.blueAccent[800]}55` } },
  '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${colors.grey[700]}`, backgroundColor: colors.blueAccent[700], color: colors.grey[100] },
  '& .MuiTablePagination-root': { color: colors.grey[100] },
  '& .MuiIconButton-root': { color: colors.grey[200] },
});

const SurveyResultsReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/survey-reports/trips-with-feedback`, {
          headers: { token },
        });
        const data = await res.json();
        if (data.success) setTrips(data.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [token]);

  const handleTripChange = async (value) => {
    setSelectedTrip(value);
    setResults(null);
    setError('');
    if (!value) return;

    const [countryId, departureDate] = value.split('|');
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/survey-reports/trip-survey-results?countryId=${countryId}&departureDate=${departureDate}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setResults(data.data);
      } else {
        setError(data.message || 'Failed to load survey results');
      }
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const selectedTripLabel = (() => {
    if (!selectedTrip) return '';
    const [countryId, departureDate] = selectedTrip.split('|');
    const trip = trips.find((t) => String(t.countryId) === countryId && t.departureDate === departureDate);
    return trip ? `${trip.destination} — ${trip.departureDate}` : selectedTrip;
  })();

  return (
    <Box m="20px">
      <Header title="Survey Results" subtitle="Passenger feedback responses organized by trip" />

      <Card
        sx={{
          mb: 3,
          background: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" fontWeight="600" sx={{ color: colors.grey[100], mb: 1 }}>
            Select trip
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2 }}>
            Only trips with at least one submitted feedback response are shown.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8} md={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel shrink sx={labelSx(colors)}>Trip</InputLabel>
                <Select
                  label="Trip"
                  displayEmpty
                  value={selectedTrip}
                  onChange={(e) => handleTripChange(e.target.value)}
                  sx={inputSx(colors)}
                  renderValue={(v) =>
                    v === '' ? (
                      <Typography component="span" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>
                        Choose a trip…
                      </Typography>
                    ) : (
                      selectedTripLabel || v
                    )
                  }
                >
                  <MenuItem value=""><em>Choose a trip…</em></MenuItem>
                  {trips.map((t) => (
                    <MenuItem key={`${t.countryId}|${t.departureDate}`} value={`${t.countryId}|${t.departureDate}`}>
                      {t.destination} — {t.departureDate}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: colors.greenAccent[400] }} />
        </Box>
      ) : null}

      {!loading && results ? (
        <>
          {results.totalResponses === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>No feedback responses found for this trip.</Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard icon={PeopleOutlinedIcon} label="Total responses" value={results.totalResponses} colors={colors} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard
                    icon={StarIcon}
                    label="Overall avg rating"
                    value={results.overallAvgRating != null ? `${results.overallAvgRating} / 5` : '—'}
                    colors={colors}
                  />
                </Grid>
              </Grid>

              <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${colors.grey[700]}`, overflow: 'hidden', backgroundColor: colors.primary[400] }}>
                <Box px={2} py={1.5} borderBottom={`1px solid ${colors.grey[700]}`}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: colors.grey[100] }}>
                    Passenger responses
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    Click <strong>View</strong> to see individual answers for each passenger.
                  </Typography>
                </Box>
                <Box height={450} sx={gridShellSx(colors)}>
                  <DataGrid
                    rows={results.passengers || []}
                    getRowId={(r) => r.feedback_id}
                    disableRowSelectionOnClick
                    rowsPerPageOptions={[10, 25, 50]}
                    pageSize={10}
                    columns={[
                      { field: 'passenger', headerName: 'Passenger', flex: 1, minWidth: 160 },
                      { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
                      {
                        field: 'avg_rating',
                        headerName: 'Avg Rating',
                        width: 120,
                        headerAlign: 'center',
                        align: 'center',
                        valueFormatter: (params) => params.value != null ? `${params.value} ★` : '—',
                      },
                      { field: 'submittedAt', headerName: 'Submitted At', width: 155 },
                      {
                        field: 'actions',
                        headerName: 'Detail',
                        width: 100,
                        sortable: false,
                        headerAlign: 'center',
                        align: 'center',
                        renderCell: (params) => (
                          <Button
                            size="small"
                            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                            onClick={() => navigate(`/userfeedback/${params.row.feedback_id}`)}
                            sx={{ color: colors.greenAccent[400], textTransform: 'none', minWidth: 0 }}
                          >
                            View
                          </Button>
                        ),
                      },
                    ]}
                    localeText={{ noRowsLabel: 'No passenger responses found.' }}
                    sx={{ border: 'none', '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                  />
                </Box>
              </Paper>
            </>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default SurveyResultsReport;
